import { patchUser } from "@/api/privateRequests/user/patchUser";
import Button from "@/components/Button";
import { CancelButton } from "@/components/atoms/Buttons/CancelButton";
import { rootCanonicalUrl } from "@/config/canonical";
import { HeroSection } from "@/containers/HeroSection";
import { Dialog } from "@/design-system/Dialog/Dialog";
import { Switch } from "@/design-system/Switch/Switch";
import {
  trackAutomaticUpdates,
  trackManagePlan,
  trackNavigateOnboardingSteps,
  trackProfileUpgrade,
} from "@/events/profileMixpanel";
import { cn } from "@/lib/utils";
import {
  FEEDBACK_PORTAL_DISPLAY_NAME_REGEX,
  buildFeaturebaseRedirectUrl,
  validateFeedbackPortalDisplayName,
} from "@/pages/FeaturebaseSso/featureBaseFunctions";
import { deleteAccount } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import type { BillingProvider } from "@repo/shared/redux";
import { useEffect, useState } from "react";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { NavLink } from "react-router";

import { createCustomerPortalSession } from "../../api/privateApi";
import {
  pc as pcIcon,
  playstation as playstationIcon,
  smallInfo,
  subscriberOnboardingIcon,
  xbox as xboxIcon,
} from "../../designSystemIcons";
import { addAlert } from "../../redux/message/messageSlice";
import { clearSbcParams } from "../../redux/sbcs/sbcFiltersSlice";
import { clearSbcSets, fetchSbcsSets } from "../../redux/sbcs/sbcSetsSlice";
import { AppDispatch } from "../../redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
  logoutUser,
  setUpdateUser,
  unlinkClub,
  updateUser,
} from "../../redux/user/userSlice";

type ProfileSubscription = {
  name: "Free" | "Pro" | "Elite" | "Ultimate";
  color: string;
};
// Where a store subscriber has to cancel
const STORE_NAMES: Record<BillingProvider, string | null> = {
  stripe: null,
  app_store: "App Store",
  play_store: "Google Play",
};
const Profile = () => {
  const { t } = useTranslation(["profile", "auth", "common"]);
  const { user, status } = useSelector(getUserSelector);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<ProfileSubscription>(
    {} as ProfileSubscription,
  );
  const [feedbackDisplayName, setFeedbackDisplayName] = useState("");
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [isRedirectingFeedback, setIsRedirectingFeedback] = useState(false);
  const [showAutoImportsInfo, setShowAutoImportsInfo] = useState(false);
  const [showUnlinkInfo, setShowUnlinkInfo] = useState(false);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isRenewing = user.paid && !user.cancelsAt;
  const store = user.billingProvider && STORE_NAMES[user.billingProvider];
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const stepsCompleted =
    useSelector((s: any) => s.userReducer.user?.completedOnboarding) ?? [];
  const requiredSubscriberOnboardingSteps =
    user.subscription === "gold" ? 4 : user.subscription === "diamond" ? 6 : 0;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setFeedbackDisplayName(user.feedbackPortalDisplayName ?? "");
  }, [user.feedbackPortalDisplayName]);

  useEffect(() => {
    let subscriptionColor: string;
    let subscriptionName: "Free" | "Pro" | "Elite" | "Ultimate";

    if (user.subscription === "silver") {
      subscriptionColor =
        "linear-gradient(101.12deg, rgb(39, 205, 186) 0%, rgb(4, 136, 123) 100%)";
      subscriptionName = "Pro";
    } else if (user.subscription === "gold") {
      subscriptionName = "Elite";
      subscriptionColor =
        "linear-gradient(101.12deg, rgb(255, 115, 123) 0%, rgb(233, 48, 64) 100%)";
    } else if (user.subscription === "diamond") {
      subscriptionName = "Ultimate";
      subscriptionColor =
        "linear-gradient(101.12deg, rgb(255, 161, 47) 0%, rgb(254, 70, 70) 100%)";
    } else {
      subscriptionName = "Free";
      subscriptionColor = "var(--color-primary-light)";
    }
    setSubscription({ name: subscriptionName, color: subscriptionColor });
  }, [user.subscription]);

  const userSubCancelsAtDate =
    user.cancelsAt && new Date(user.cancelsAt * 1000).toDateString();

  const userStatusMessage = userSubCancelsAtDate
    ? t("statusActiveExpires", { date: userSubCancelsAtDate })
    : t("statusActive");

  const logout = () => {
    dispatch(clearSbcSets());
    dispatch(clearSbcParams());
    dispatch(logoutUser()).then((_) => {
      navigate("/");
      dispatch(fetchSbcsSets());
    });
  };

  const handleUnlinkClub = async () => {
    setIsUnlinking(true);
    try {
      await dispatch(unlinkClub()).unwrap();
      dispatch(
        addAlert({
          title: t("alerts.clubUnlinked"),
          description: t("alerts.clubUnlinkedDescription"),
          type: "success",
        }),
      );
    } catch {
      dispatch(
        addAlert({
          title: t("alerts.unlinkFailed"),
          description: t("alerts.unlinkFailedDescription"),
          type: "error",
        }),
      );
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      dispatch(
        addAlert({
          title: t("alerts.accountDeleted"),
          description: t("alerts.accountDeletedDescription"),
          type: "success",
        }),
      );
      logout();
    } catch {
      dispatch(
        addAlert({
          title: t("alerts.deleteFailed"),
          description: t("alerts.deleteFailedDescription"),
          type: "error",
        }),
      );
      setIsDeleting(false);
    }
  };

  const redirectToFeedbackPortal = async () => {
    let didNavigate = false;
    try {
      setIsRedirectingFeedback(true);
      const featurebaseUrl = await buildFeaturebaseRedirectUrl();
      didNavigate = true;
      window.location.href = featurebaseUrl;
    } catch (err) {
      console.error("Failed to redirect to Feedback Portal:", err);
      setFeedbackError(t("feedbackOpenFailed"));
    } finally {
      // If navigation succeeds, the browser will unload this page; avoid clearing the
      // loading UI early (it can flicker off before the redirect completes).
      if (!didNavigate) {
        setIsRedirectingFeedback(false);
      }
    }
  };

  const handleFeedbackPortalUpdate = async () => {
    const { trimmed, isValid, error } =
      validateFeedbackPortalDisplayName(feedbackDisplayName);

    if (!trimmed) {
      setFeedbackError(t("feedbackEnterName"));
      return;
    }

    if (!isValid) {
      setFeedbackError(error);
      return;
    }

    setFeedbackError(null);
    setIsSavingFeedback(true);

    try {
      if (trimmed !== user.feedbackPortalDisplayName) {
        await patchUser({ feedbackPortalDisplayName: trimmed });
        dispatch(setUpdateUser({ feedbackPortalDisplayName: trimmed }));
      }

      await redirectToFeedbackPortal();
    } catch (err) {
      console.error("Failed to update Feedback Portal display name:", err);
      setFeedbackError(t("feedbackUpdateFailed"));
    } finally {
      setIsSavingFeedback(false);
    }
  };

  useEffect(() => {
    if (!user.uuid && status === "rejected") {
      dispatch(
        addAlert({
          description: t("alerts.fetchFailed"),
          type: "error",
        }),
      );
    }
  }, [navigate, user.uuid, status]);

  return (
    <main
      className="min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, #1C2A37 0%, #161C21 100%), #1D1D1D",
      }}
    >
      <HeroSection
        helmetTitle={t("common:profile")}
        helmetCanonicalLink={rootCanonicalUrl("/profile")}
        showOnTouchDeviceOnly={true}
      />
      <div className="mx-auto flex flex-col gap-y-4 pt-4 pb-12 text-sm desktop:w-[560px] desktop:gap-y-6 desktop:p-4 desktop:text-sm">
        <section className="flex flex-col gap-y-1">
          <p className="ml-4 text-xs text-dark-tertiary">
            {t("accountInformation")}
          </p>
          <div
            className="flex flex-col gap-y-4 px-4 py-3 desktop:rounded-lg"
            style={{
              background: "rgba(255, 255, 255, 0.10)",
            }}
          >
            <div className="flex flex-row justify-between">
              <p className="text-white">{t("common:email")}</p>
              <p className="text-secondary">
                {user.email?.length > 36
                  ? user.email.substring(0, 36) + "..."
                  : user.email}
              </p>
            </div>
            <div className="flex flex-row justify-between">
              <p className="text-white">{t("subscription")}</p>
              <div className="flex items-center">
                <div
                  className={"my-auto mr-2 h-4 w-4 rounded-full"}
                  style={{ background: subscription.color }}
                />
                <p className="text-secondary">{subscription.name}</p>
              </div>
            </div>
            {user.paid && (
              <div className="flex flex-row justify-between">
                <p className="text-white">{t("status")}</p>
                <p className="text-secondary">
                  {user.subscription === "free" ? "" : userStatusMessage}
                </p>
              </div>
            )}
          </div>
          <div className="my-2 ml-auto mr-2 mobile:mr-0 flex flex-col gap-y-1">
            <Button
              variant={isRenewing ? "tertiary" : "primary"}
              onClick={async () => {
                if (!user.paid) {
                  trackProfileUpgrade();
                  navigate("/subscription");
                  return;
                }
                trackManagePlan();
                createCustomerPortalSession();
              }}
              label={
                !user.paid
                  ? t("common:upgrade")
                  : user.cancelsAt
                    ? t("reactivatePlan")
                    : t("managePlan")
              }
            />
            {isRenewing && <CancelButton />}
          </div>
        </section>
        <section className="flex flex-col gap-y-1">
          <p className="ml-4 text-xs text-dark-tertiary">
            {t("accountSettings")}
          </p>
          <div
            className="flex flex-col gap-y-4 px-4 py-3 desktop:rounded-lg"
            style={{
              background: "rgba(255, 255, 255, 0.10)",
            }}
          >
            <div className="flex flex-row justify-between gap-x-2">
              <p className="text-white">{t("common:emailNewsOptIn")}</p>
              <Switch
                onChange={(isActive) => {
                  dispatch(updateUser({ emailNotification: isActive }));
                  dispatch(
                    addAlert({
                      title: isActive
                        ? t("alerts.emailNotificationsEnabled")
                        : t("alerts.emailNotificationsDisabled"),
                      description: t("alerts.emailNotificationsDescription"),
                      type: "success",
                    }),
                  );
                }}
                isActive={user.emailNotification}
              />
            </div>
            {canUseClubImport && (
              <div className="flex flex-row justify-between">
                <p className="flex items-center text-white">
                  <span>{t("allowAutomaticImports")}</span>
                  <button
                    className="p-2"
                    onClick={() => setShowAutoImportsInfo(true)}
                  >
                    {smallInfo}
                  </button>
                </p>
                <Switch
                  onChange={(isActive) => {
                    trackAutomaticUpdates(isActive);
                    dispatch(updateUser({ allowTracking: isActive }));
                    dispatch(
                      addAlert({
                        title: isActive
                          ? t("alerts.trackingAllowed")
                          : t("alerts.trackingDisabled"),
                        description: t("alerts.trackingDescription"),
                        type: "success",
                      }),
                    );
                  }}
                  isActive={user.allowTracking}
                />
              </div>
            )}
            {user.clubLinked && (
              <div className="flex flex-row items-center justify-between">
                <div className="flex items-center text-white">
                  <span>{t("unlinkClubImport")}</span>
                  <button
                    className="p-2"
                    onClick={() => setShowUnlinkInfo(true)}
                  >
                    {smallInfo}
                  </button>
                </div>
                <button
                  className="text-sm text-dark-tertiary underline disabled:opacity-50"
                  onClick={() => setShowUnlinkDialog(true)}
                  disabled={isUnlinking}
                >
                  {isUnlinking ? t("updating") : t("unlinkClub")}
                </button>
              </div>
            )}
            {requiredSubscriberOnboardingSteps > 0 &&
              stepsCompleted.length < requiredSubscriberOnboardingSteps && (
                <div className="flex flex-row items-center justify-between">
                  <p>
                    {t("onboardingStepsCompleted", {
                      completed: stepsCompleted.length,
                      required: requiredSubscriberOnboardingSteps,
                    })}
                  </p>
                  <NavLink
                    to="/essentials"
                    onClick={() => trackNavigateOnboardingSteps()}
                    className={cn(
                      "flex text-sm text-dark-tertiary items-center gap-1",
                    )}
                  >
                    <span className="underline">{t("completeSteps")}</span>
                    <span>{subscriberOnboardingIcon}</span>{" "}
                  </NavLink>
                </div>
              )}
            <div className="flex flex-row items-center justify-between">
              <p className="text-white">{t("yourPlatform")}</p>
              <div className="flex flex-row gap-x-4">
                <button
                  className={cn(
                    "flex w-[76px] justify-center rounded-lg border-2 bg-[#1A242D] px-2 py-[2px]",
                    user.platform === "Playstation"
                      ? "border-primary-blue"
                      : "border-transparent",
                  )}
                  onClick={() => {
                    dispatch(updateUser({ platform: "Playstation" }));
                    dispatch(
                      addAlert({
                        title: t("alerts.platformChange"),
                        description: t("alerts.platformConsole"),
                        type: "success",
                      }),
                    );
                  }}
                >
                  <div className="h-7 w-7 text-[#0097D8]">
                    {playstationIcon}
                  </div>
                  <div className="h-7 w-7">{xboxIcon}</div>
                </button>
                <button
                  className={cn(
                    "flex w-[76px] justify-center rounded-lg border-2 bg-[#1A242D] px-2 py-[2px]",
                    user.platform === "PC"
                      ? "border-primary-blue"
                      : "border-transparent",
                  )}
                  onClick={() => {
                    dispatch(updateUser({ platform: "PC" }));
                    dispatch(
                      addAlert({
                        title: t("alerts.platformChange"),
                        description: t("alerts.platformPc"),
                        type: "success",
                      }),
                    );
                  }}
                >
                  <div className="h-7 w-7">{pcIcon}</div>
                </button>
              </div>
            </div>
          </div>
        </section>
        {user.feedbackPortalDisplayName && (
          <section className="flex flex-col gap-y-1">
            <p className="ml-4 text-xs text-dark-tertiary">
              {t("feedbackPortal")}
            </p>
            <div
              className="flex flex-col gap-y-4 px-4 py-3 desktop:rounded-lg"
              style={{
                background: "rgba(255, 255, 255, 0.10)",
              }}
            >
              <div className="flex flex-row items-center gap-x-3">
                <p className="text-white whitespace-nowrap">
                  {t("auth:featurebase.displayName")}
                </p>
                <div className="flex flex-1 items-center justify-end gap-x-2 min-w-0">
                  <input
                    className="w-full max-w-48 min-w-0 rounded-md border border-gray-600 bg-[#1A242D] px-3 py-2 text-white outline-none focus:border-primary-blue"
                    value={feedbackDisplayName}
                    onChange={(e) => setFeedbackDisplayName(e.target.value)}
                    placeholder={t("auth:featurebase.displayNamePlaceholder")}
                    maxLength={30}
                    pattern={FEEDBACK_PORTAL_DISPLAY_NAME_REGEX.source}
                  />
                  <button
                    className="flex items-center gap-1 text-dark-tertiary underline text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleFeedbackPortalUpdate}
                    disabled={isSavingFeedback || isRedirectingFeedback}
                  >
                    {isSavingFeedback || isRedirectingFeedback
                      ? t("updating")
                      : t("common:update")}
                    {isSavingFeedback || isRedirectingFeedback ? (
                      <Oval
                        width="14"
                        height="14"
                        stroke="#9CA3AF"
                        className="shrink-0"
                      />
                    ) : (
                      <span className="inline-block">
                        {subscriberOnboardingIcon}
                      </span>
                    )}
                  </button>
                </div>
              </div>
              {(isSavingFeedback || isRedirectingFeedback) && (
                <p className="text-xs text-dark-tertiary" role="status">
                  {t("redirectingToFeedbackPortal")}
                </p>
              )}
              {feedbackError && (
                <p className="text-xs text-error" role="alert">
                  {feedbackError}
                </p>
              )}
            </div>
          </section>
        )}
        <section className="mx-auto mt-8 flex w-[300px] flex-col items-center gap-y-4">
          <div className="flex flex-row gap-x-2">
            <Button
              variant="tertiary"
              onClick={() => navigate("/contact")}
              label={t("contactUs")}
            />

            <Button
              variant="tertiary"
              onClick={logout}
              label={t("common:logOut")}
            />
          </div>
          <button
            className="text-xs text-dark-tertiary underline hover:text-primary-red disabled:opacity-50"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isDeleting}
          >
            {isDeleting
              ? t("accountDeletion.deleting")
              : t("accountDeletion.button")}
          </button>
        </section>
      </div>
      <Dialog
        isOpen={showAutoImportsInfo}
        onClose={() => setShowAutoImportsInfo(false)}
        title={t("allowAutomaticImports")}
        negLabel=""
        posLabel={t("common:understood")}
      >
        <p className="mt-2">{t("automaticImportsTooltip")}</p>
      </Dialog>
      <Dialog
        isOpen={showUnlinkInfo}
        onClose={() => setShowUnlinkInfo(false)}
        title={t("unlinkClubImport")}
        negLabel=""
        posLabel={t("common:understood")}
      >
        <p className="mt-2">{t("unlinkDialog.removed")}</p>
        <p className="mt-2">{t("unlinkDialog.note")}</p>
      </Dialog>
      <Dialog
        isOpen={showUnlinkDialog}
        onClose={() => setShowUnlinkDialog(false)}
        title={t("unlinkDialog.title")}
        posLabel={t("unlinkDialog.confirm")}
        onPositive={handleUnlinkClub}
      >
        <p className="mt-2">
          {t("unlinkDialog.areYouSure")} {t("unlinkDialog.cannotBeUndone")}
        </p>
        <p className="mt-2">{t("unlinkDialog.removed")}</p>
      </Dialog>
      <Dialog
        isOpen={showDeleteDialog && isRenewing}
        onClose={() => setShowDeleteDialog(false)}
        title={t("accountDeletion.unsubscribeTitle")}
        negLabel={t("common:understood")}
        posLabel=""
      >
        <p className="mt-2">
          {store
            ? t("accountDeletion.unsubscribeInStore", { store })
            : t("accountDeletion.unsubscribe")}
        </p>
      </Dialog>
      <Dialog
        isOpen={showDeleteDialog && !isRenewing}
        onClose={() => setShowDeleteDialog(false)}
        title={t("accountDeletion.title")}
        posLabel={t("accountDeletion.button")}
        danger
        onPositive={handleDeleteAccount}
      >
        <p className="mt-2">{t("accountDeletion.description")}</p>
      </Dialog>
    </main>
  );
};
export default Profile;
